// EditMenu — the peek's Edit home (ADR-036 §7): the entity's EDIT rows, read
// from its registry (`editsOf(acts)`). ONE row renders as a plain quiet button
// named by the act, text only (the peek stays calm — its buttons carry no glyph
// and never the brand fill); SEVERAL fold into "Edit ▾" — the labelled door
// shape, the rows with their glyphs, in the registry's order. A row with
// `choices` opens the one-decision chooser first (ADR-038 §1): the dialog is
// mounted HERE, once per surface, so an entity module owns only its drawers.
// Absent when the viewer may edit nothing (an empty set renders nothing — the
// panel then draws no top row). `ContextPanel` composes it; a surface never
// places it by hand. Rung-2 composite, props-only: the acts arrive resolved;
// the only state is the open chooser.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/Button.js';
import { ActionMenu } from './ActionMenu.js';
import { ChoiceDialog } from './ChoiceDialog.js';
import { runAct, toMenuRows, type EntityAct } from './entityRegistry.js';

export interface EditMenuProps {
  /** The edit rows — `editsOf(acts)`. */
  acts: readonly EntityAct[];
  /** The menu's name ("Edit") — the trigger's text when several rows fold. */
  label: string;
}

export function EditMenu({ acts, label }: EditMenuProps) {
  const navigate = useNavigate();
  // The chooser row whose question is open (one dialog, kept mounted so it animates out).
  const [chooser, setChooser] = useState<EntityAct | null>(null);
  if (acts.length === 0) return null;

  const dialog = (
    <ChoiceDialog
      open={chooser !== null}
      onClose={() => setChooser(null)}
      title={chooser?.question ?? ''}
      choices={chooser?.choices ?? []}
      onChoose={(id) => {
        const choice = chooser?.choices?.find((c) => c.id === id);
        if (choice) runAct(choice.run, navigate);
      }}
    />
  );

  if (acts.length === 1) {
    const act = acts[0]!;
    return (
      <>
        <Button
          variant="secondary"
          disabled={act.disabled}
          onClick={() => (act.choices ? setChooser(act) : act.run && runAct(act.run, navigate))}
        >
          {act.label}
        </Button>
        {act.choices && dialog}
      </>
    );
  }

  return (
    <>
      <ActionMenu label={label} items={toMenuRows(acts, setChooser)} trigger="labelled" placement="bottom start" />
      {acts.some((a) => a.choices) && dialog}
    </>
  );
}
